import * as styles from "./Accordion.module.css";

import React, { useEffect, useRef, useState } from "react";

import { ButtonLink } from "@components/ui";
import { HtmlOutput } from "@components/html";
import { IAccordionProps } from "@models/component-props";

/**
 * Accordion component that displays collapsible sections with titles and content.
 *
 * @param {IAccordionProps} props - The component props.
 * @param {string} props.pageTitle - The title of the page.
 * @param {string[]} props.contentImages - An array of image URLs used in content rendering.
 * @param {{ title: string; content: string; button?: { to: string; title: string; style: string } }[]} props.items - The array of accordion items.
 * @return {JSX.Element} The rendered accordion component.
 */
const Accordion = ({
  pageTitle,
  contentImages,
  items,
}: IAccordionProps): JSX.Element => {
  // Ensure `items` is an array
  const itemsArr = items ? items : [];

  // Create refs for each accordion item
  const refs = useRef(itemsArr.map(() => React.createRef<HTMLDivElement>()));

  // Default state of all accordions (closed)
  const defAccordionsOpen = itemsArr.map(() => false);

  // State to track which accordions are open
  const [accordionsOpen, setAccordionsOpen] = useState(defAccordionsOpen);

  // Focus on the first accordion item on component mount
  useEffect(() => {
    if (refs.current.length > 0) {
      refs.current[0].current && refs.current[0].current.focus();
    }
  }, []);

  return (
    <div className={styles.accordionContainer}>
      {itemsArr.map((item, i) => (
        <div
          key={`AccordionItem_${i}`}
          className={`${styles.accordionItem} ${
            accordionsOpen[i] ? styles.open : ""
          }`}
        >
          {/* Button to toggle accordion open/close */}
          <button
            type="button"
            className={`${styles.accordionButton} ${
              i === 0 || (i > 0 && accordionsOpen[i - 1])
                ? styles.thickerTop
                : ""
            }`}
            aria-label={item.title}
            onClick={() => {
              const btnOpen = !accordionsOpen[i];
              const newAccordionsOpen = accordionsOpen.map((acc, x) =>
                x === i ? btnOpen : acc
              );
              setAccordionsOpen(newAccordionsOpen);
            }}
          >
            {item.title}
          </button>

          {/* Accordion content */}
          <div
            ref={refs.current[i]}
            className={styles.accordionContent}
            style={{
              maxHeight: `${
                accordionsOpen[i]
                  ? `${
                      (refs.current[i].current as HTMLDivElement).scrollHeight
                    }px`
                  : ""
              }`,
            }}
          >
            <HtmlOutput
              title={pageTitle}
              contentImages={contentImages}
              html={item.content}
            />
            {item.button && (
              <ButtonLink
                link={item.button.to}
                title={item.button.title}
                buttonStyle={item.button.style}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Accordion;
